import Button from '../buttons/button'
import ContentTile from '../content/ContentTile'
import Tag from '../badgesAndTags/tag'
import TypographyParagraph from '../typography/TypographyParagraph'
import ContentDisclosure from './contentDisclosure.tsx'
import { cidData } from '#types/loreTypes.tsx'
import { getMetadataProfileURL } from '#app/utils/misc.tsx'
import InternalLink from './internalLink.tsx'
import { useState } from 'react'
import { ArrowRightIcon, MoreIcon } from '../icons/index.ts'
import VideoPlaceholder from '#images/video-placeholder.jpg'

const formatDate = (date: string) => {
	try {
		const options = { day: 'numeric', month: 'short', year: 'numeric' }
		const dateObj = new Date(date)
		//@ts-ignore
		return dateObj.toLocaleDateString('en-US', options)
	} catch (e) {
		return date
	}
}

const paginationAmount = 20

export default function CIDsTable({ data }: { data: cidData[] }) {
	const [paginationIndex, setPaginationIndex] = useState(1)
	const [dataToShow, setDataToShow] = useState(
		data && data.length
			? data.slice(0, paginationIndex * paginationAmount)
			: undefined,
	)

	const handleIncrementPagination = () => {
		const newIndex = paginationIndex + 1
		setPaginationIndex(newIndex)
		setDataToShow(data.slice(0, newIndex * paginationAmount))
	}

	return (
		<>
			{dataToShow && dataToShow.length ? (
				<div className="flex flex-col gap-y-4">
					{dataToShow.map((cidData: cidData, index) => {
						const metadataProfileURL = getMetadataProfileURL(
							cidData.attestation,
						)
						let nftProfileURL = ''
						if (
							cidData.attestation.template.slug === 'collection' ||
							cidData.attestation.originalReference?.template?.slug ===
								'collection' ||
							cidData.attestation.references.entry?.nft?.hasOwnProperty(
								'collection',
							)
						) {
							const { chain, contractAddress, tokenId } = cidData
							if (chain) {
								nftProfileURL = `/nftProfile/${chain}${chain != 'solana' ? '/' + contractAddress : ''}/${tokenId}`
							}
						}
						return (
							<ContentDisclosure
								key={`${cidData.cid}-${index}`}
								button={
									<ContentTile
										key={index}
										title={cidData.name}
										description={
											cidData.format && cidData.date
												? cidData.format + ' • ' + formatDate(cidData.date)
												: ''
										}
										image={
											cidData.format && cidData.format.includes('video')
												? VideoPlaceholder
												: cidData.src
										}
										isVerified={cidData.isVerified}
										showCaret={false}
										showBorder={false}
										variant="collection"
									/>
								}
								content={
									<div className="flex flex-col gap-y-2">
										{cidData.status && (
											<div className="flex flex-row items-center">
												<Tag showIcon={false}>COUNTERSIGN</Tag>
												<TypographyParagraph
													size="medium"
													additionalClasses="ml-2 text-textPrimaryLight dark:text-textPrimaryDark"
												>
													{cidData.status}
												</TypographyParagraph>
											</div>
										)}
										{cidData.cid && (
											<div className="flex flex-row items-center">
												<Tag showIcon={false}>IPFS-CID</Tag>
												<TypographyParagraph
													size="medium"
													additionalClasses="ml-2 text-textPrimaryLight dark:text-textPrimaryDark break-all truncate"
												>
													{cidData.cid}
												</TypographyParagraph>
											</div>
										)}
										{cidData.chain && cidData.chain !== '' && (
											<div className="flex flex-row items-center">
												<Tag showIcon={false}>CHAIN</Tag>
												<TypographyParagraph
													size="medium"
													additionalClasses="ml-2 text-textPrimaryLight dark:text-textPrimaryDark"
												>
													{cidData.chain}
												</TypographyParagraph>
											</div>
										)}
										{cidData.contractAddress &&
											cidData.contractAddress.length && (
												<div className="flex flex-row items-center">
													<Tag showIcon={false}>CONTRACT</Tag>
													<TypographyParagraph
														size="medium"
														additionalClasses="ml-2 text-textPrimaryLight dark:text-textPrimaryDark break-all truncate"
													>
														{cidData.contractAddress}
													</TypographyParagraph>
												</div>
											)}
										{cidData.tokenId !== '' && (
											<div className="flex flex-row items-center">
												<Tag showIcon={false}>TOKEN ID</Tag>
												<TypographyParagraph
													size="medium"
													additionalClasses="ml-2 text-textPrimaryLight dark:text-textPrimaryDark break-all truncate"
												>
													{cidData.tokenId}
												</TypographyParagraph>
											</div>
										)}
										{(cidData.attestation.template.slug === 'collection' ||
											cidData.attestation.originalReference?.template?.slug ===
												'collection' ||
											cidData.attestation.references.entry?.nft?.hasOwnProperty(
												'collection',
											)) &&
											nftProfileURL !== '' && (
												<InternalLink
													additionalClasses="flex flex-row justify-start text-center"
													to={nftProfileURL}
													prefetch="none"
												>
													<Button
														variant="tertiary"
														size="medium"
														// additionalClasses="mt-4"
														rightIcon={<ArrowRightIcon />}
													>
														View Token Profile
													</Button>
												</InternalLink>
											)}
										{cidData.chain &&
											cidData.chain !== '' &&
											(cidData.chain !== 'solana'
												? cidData.contractAddress &&
													cidData.contractAddress.length
												: true) &&
											cidData.tokenId !== '' &&
											cidData.referenceCid && (
												<div className="flex flex-row items-center gap-x-4">
													{cidData.referenceCid != 'Pending' &&
														cidData.cid !== undefined &&
														metadataProfileURL &&
														metadataProfileURL !== '' && (
															<InternalLink
																additionalClasses="flex flex-row justify-center text-center"
																to={metadataProfileURL}
																prefetch="none"
															>
																<Button
																	variant="tertiary"
																	size="medium"
																	additionalClasses="mt-4"
																	rightIcon={<ArrowRightIcon />}
																>
																	View provenance
																</Button>
															</InternalLink>
														)}
												</div>
											)}
									</div>
								}
							/>
						)
					})}
					{dataToShow && dataToShow.length < data.length && (
						<div className="flex w-full items-center justify-center">
							<Tag showIcon={false} onClick={() => handleIncrementPagination()}>
								<MoreIcon />
							</Tag>
						</div>
					)}
				</div>
			) : (
				<div className="mt-12 flex flex-col items-center justify-center">
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						No related CIDs.
					</TypographyParagraph>
				</div>
			)}
		</>
	)
}
